<template>
    <v-container fluid class="main-grid">
        <div class="edit-content">
            <div class="tgk-paragraph-text">{{ selectedItem.$.title }}</div>
            <div class="tgk-paragraph-text">{{ selectedItem.$.orig_filename }}</div>
            <div class="tgk-paragraph-text">Original: {{ selectedItem.width }}x{{ selectedItem.height }}px</div>
            <div class="mt-5 mb-5 d-flex flex-row gap-1">
                <a :href="selectedItem.$.url" target="_blank" class="text-center">
                    <img
                        :src="selectedItem.$.url"
                        :alt="selectedItem.$.title || selectedItem.$.caption"
                        style="max-height: 250px; max-width: 100%"
                    />
                </a>
                <div class="d-flex flex-column gap-1">
                    <IconBtn
                        color="primary "
                        icon-cls="rotate-anticlockwise"
                        icon-color="white"
                        :as-icon="false"
                        tooltip="90° links"
                        @click="rotateImage(90)"
                    />
                    <IconBtn
                        color="primary "
                        icon-cls="rotate-clockwise"
                        icon-color="white"
                        :as-icon="false"
                        tooltip="90° rechts"
                        @click="rotateImage(-90)"
                    />
                </div>
            </div>
            <v-divider />
            <v-form class="mt-5">
                <v-row dense>
                    <v-col cols="12">
                        <LabelField :label="$t('dateiname')">
                            <v-text-field
                                :value="selectedItem.orig_filename"
                                dense
                                outlined
                                hide-details="auto"
                                @input="onInput('orig_filename', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row v-if="userIsAdmin()" dense>
                    <v-col cols="12">
                        <LabelField :label="$t('staff_title')">
                            <v-text-field
                                :value="selectedItem.staff_title"
                                dense
                                outlined
                                hide-details="auto"
                                @input="onInput('staff_title', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <LabelField :label="$t('copyright')">
                            <v-text-field
                                :value="selectedItem.credits"
                                dense
                                outlined
                                hide-details="auto"
                                prepend-inner-icon="ic-16 ic-copyright ic-col-black"
                                :placeholder="`${new Date().getFullYear()} ...`"
                                @input="onInput('credits', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <LabelField :label="$t('caption')">
                            <v-textarea
                                :value="selectedItem.caption"
                                dense
                                hide-details="auto"
                                outlined
                                :placeholder="$t('caption_placeholder')"
                                rows="3"
                                @input="onInput('caption', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <LabelField :label="$t('titel_alt')">
                            <v-text-field
                                :value="selectedItem.title"
                                dense
                                outlined
                                hide-details="auto"
                                :placeholder="$t('alt_text_desc')"
                                @input="onInput('title', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
            </v-form>
        </div>
        <v-row dense class="toolbar-content">
            <v-col cols="2">
                <ActionBtn :loading="saving" @click="onSaveBtn">{{ $t('save') }}</ActionBtn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { Image } from '@/models/Image';
import { errToStr } from '@/helpers/tools';
import { mapActions } from 'vuex';
import { userIsAdmin } from '@/helpers/auth';
import { instance as api } from '@/helpers/api';

/**
 * Image detail view
 * Emits
 * $emit('image-updated', new Image(res.data.record));
 * when an image record is updated (e.g. due to rotation, saving etc.)
 */
export default {
    name: 'MediaManagerDetailView',
    props: {
        selectedItem: {
            type: Image,
            required: true,
        },
    },

    data() {
        return {
            saving: false,
        };
    },

    methods: {
        userIsAdmin,
        ...mapActions(['runAsync']),
        ...mapActions('image', ['loadImage']),
        onInput(field, value) {
            this.selectedItem.set(field, value);
        },
        onSaveBtn() {
            this.save();
        },
        async save() {
            // nothing to do, so don't save:
            if (!this.selectedItem.changed()) {
                return;
            }

            this.saving = true;

            try {
                await this.runAsync(() => this.selectedItem.save());
                await this.loadImage({ imageId: this.selectedItem.id, force: true });
                this.$emit('image-updated', this.selectedItem);
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.saving = false;
            }
        },

        async rotateImage(angle) {
            this.saving = true;

            try {
                const res = await this.runAsync(() => api().post('/image/rotate', { angle, id: this.selectedItem.id }));
                if (res?.data?.record) {
                    this.selectedItem.set(res.data.record);
                }
                await this.loadImage({ imageId: this.selectedItem.id, force: true });
                this.$emit('image-updated', new Image(res.data.record));
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-grid {
    display: grid;
    height: 100%;
    grid-template-columns: auto;
    grid-template-rows: 1fr auto;
    grid-template-areas:
        'content'
        'toolbar';
}

.edit-content {
    grid-area: content;
    overflow-y: auto;
    overflow-x: hidden;
}
.toolbar-content {
    grid-area: toolbar;
}
</style>
